import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Mic, FileText, Hash, Settings, Share2, History, CheckCircle2, ClipboardList, Layout, Zap, ArrowRight, MousePointer, Palette, Heart, ShieldCheck } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

// Provider Tutorial Slides with REAL screenshots
const providerSlides = [
  {
    id: 1,
    title: "Getting Started",
    content: "Click \"I'm a Provider\" to access the full clinical workflow with notes, referrals, imaging orders, and ICD-10 detection.",
    icon: <FileText className="w-8 h-8 text-primary" />,
    image: "/tutorial/real_role_selection.png",
    highlight: "Click the Provider card on the left"
  },
  {
    id: 2,
    title: "Header Toolbar",
    content: "Your quick-access toolbar:\n• Speed — batch charting mode\n• A a — text size controls\n• Mobile / Desktop layout toggle\n• 2 Col — side-by-side view\n• History, Team, Update, Theme",
    icon: <Settings className="w-8 h-8 text-emerald-400" />,
    image: "/tutorial/real_header_controls.png",
    highlight: "Toolbar runs across the top below the nav"
  },
  {
    id: 3,
    title: "Voice or Type — No Forced Fields",
    content: "Hit the gold mic to dictate or type directly.\n\n✨ Voice-first workflow: just say \"25 year old female runny nose\" and Age + Sex auto-populate. No mandatory typing.\n\nAuto-detects: age, sex, status, reason for visit, and ICD-10 — all from your notes.",
    icon: <Mic className="w-8 h-8 text-red-400" />,
    image: "/tutorial/real_notes_input.png",
    highlight: "Emerald dot = auto-detected from voice"
  },
  {
    id: 4,
    title: "Recording Tips",
    content: "• Pause if needed — hit play to resume\n• Audio bars show mic level\n• 21MB chime = heads up\n• 24MB = auto-stop (saved)\n\nBackup text option if transcription fails.",
    icon: <Mic className="w-8 h-8 text-amber-400" />,
  },
  {
    id: 5,
    title: "Quick Context — Auto-Filled",
    content: "Populated from your dictation, zero clicks:\n• Age + Sex (from \"25 F\", \"25 year old female\", etc.)\n• Reason for Visit\n• Chief Complaint\n\n🟢 Emerald dot = auto-detected. Type over it any time to override — the dot clears when you take control.",
    icon: <Settings className="w-8 h-8 text-emerald-400" />,
    cinematic: true,
    cinematicVisual: 'autodetect',
  },
  {
    id: 6,
    title: "ICD-10 Codes",
    content: "Codes auto-detect as you type/dictate.\n\n• Green badges = detected live\n• Click to mark PRIMARY (1°)\n• Drag to reorder priority\n• Smart ICD-10 Search for manual add",
    icon: <Hash className="w-8 h-8 text-emerald-400" />,
  },
  {
    id: 7,
    title: "Create Docs — One Click",
    content: "Click Create Docs → all outputs at once:\n• Clinical SOAP Note\n• Referral Indications\n• Imaging Justifications\n• Admin Letter\n• Functional Limitations",
    icon: <FileText className="w-8 h-8 text-blue-400" />,
    image: "/tutorial/real_output_panel.png",
    highlight: "Note · Referral · Letter · Limits tabs"
  },
  {
    id: 8,
    title: "Layout Options",
    content: "Toggle in the toolbar:\n• Desktop — wide two-column view\n• Mobile — stacked single-column\n• 2 Col — always side-by-side\n\nDaily Schedule slots at the top for patient tracking.",
    icon: <Layout className="w-8 h-8 text-purple-400" />,
    image: "/tutorial/real_column_layout.png",
    highlight: "Desktop / Mobile toggle in the toolbar",
    cinematic: true,
    cinematicZoom: true,
  },
  {
    id: 9,
    title: "The 1→5 Workflow",
    content: "ONE note becomes FIVE outputs:\n\n1. Clinical SOAP Note\n2. Referral Indications\n3. Imaging Justifications\n4. Profile / Limitations\n5. Admin Letter\n\nCopy. Paste. Done.",
    icon: <Zap className="w-8 h-8 text-yellow-400" />,
  },
  {
    id: 10,
    title: "Copy & Export",
    content: "Copy All — grabs everything at once.\n\nOr per-document:\n• Copy Note → paste into Genesis\n• Export to Word / PDF\n• Share letter via SMS link\n\nSave & Next Pt to move on.",
    icon: <CheckCircle2 className="w-8 h-8 text-green-400" />,
  },
  {
    id: 11,
    title: "Speed Charting",
    content: "Batch mode for high-volume clinic days.\n\nQueue up patients, dictate notes in sequence, generate all docs at the end.\n\nAccess from Speed button in the toolbar.",
    icon: <Zap className="w-8 h-8 text-amber-400" />,
  },
  {
    id: 12,
    title: "Mood Themes",
    content: "Before you start charting, set your mood.\n\nThis isn't decoration — it's a micro-moment of self-awareness. Military providers carry heavy cognitive loads. Choosing a theme is a quick check-in with yourself.\n\n• Eggplant — deep focus, evening energy\n• Sunset — warm, reflective calm\n• Default — mission-ready clarity\n\nSmall rituals matter. Your headspace affects your notes.",
    icon: <Heart className="w-8 h-8 text-pink-400" />,
    cinematic: true,
    cinematicVisual: 'mood',
  },
  {
    id: 13,
    title: "Legend + Compliance Kit",
    content: "Look for the \"?\" icon in the setup header — that's the Visual Signal Legend. Explains every emerald dot, cyan pill, amber HIPAA chip, and audit badge in the app.\n\nCompliance officers can grab the Compliance One-Pager, Security FAQ, and full Procurement Packet PDFs from the landing page footer — no login required.",
    icon: <ShieldCheck className="w-8 h-8 text-cyan-400" />,
  },
  {
    id: 14,
    title: "That's It!",
    content: "WriteRight PCM+ removes friction between your brain and the EHR.\n\nTalk. Generate. Paste.\nGo home on time.",
    icon: <CheckCircle2 className="w-8 h-8 text-primary" />,
  },
];

// Patient Tutorial Slides
const patientSlides = [
  {
    id: 1,
    title: "Welcome, Soldier",
    content: "WriteRight PCM+ helps you communicate your medical needs clearly — whether it's a letter, a portal message, or tracking your recovery.\n\nThis is YOUR tool. Describe it. Generate it. Bring it.",
    icon: <Heart className="w-8 h-8 text-primary" />,
  },
  {
    id: 2,
    title: "Quick Start Shortcuts",
    content: "One tap to get started:\n\n• Portal: Duty Limits — message about profile restrictions\n• Portal: Meds — refill or side effect question\n• Portal: Results — ask about lab/test results\n• Portal: Symptom — report new or worsening concern\n• Referral, FMLA, ADA, Return to Work, Command Letter",
    icon: <MousePointer className="w-8 h-8 text-amber-400" />,
  },
  {
    id: 3,
    title: "Portal Messages",
    content: "Select a portal message type and describe your situation.\n\nWriteRight drafts a clear, professional message you can copy-paste into MHS Genesis portal.\n\nYour provider sees a well-organized message instead of a wall of text.",
    icon: <FileText className="w-8 h-8 text-blue-400" />,
  },
  {
    id: 4,
    title: "Letters & Profiles",
    content: "Need a letter for your command?\n\n• Duty Limits / Profile restrictions\n• FMLA or ADA accommodations\n• Return to Work clearance\n• Letter to Commander\n\nFill in your info → describe the situation → generate the draft.",
    icon: <ClipboardList className="w-8 h-8 text-emerald-400" />,
  },
  {
    id: 5,
    title: "Voice or Type — No Forced Fields",
    content: "Hit the mic and talk naturally:\n\n\"My knee has been hurting for 3 weeks, it's worse after PT, I can't run or ruck...\"\n\nWriteRight translates your words into proper medical language. Age, sex, and reason for visit auto-fill from what you say — no forced typing.",
    icon: <Mic className="w-8 h-8 text-red-400" />,
  },
  {
    id: 6,
    title: "TBI / Concussion Recovery",
    content: "If you had a head injury or concussion:\n\nGo to Settings → Symptom Tracker (NSI) to see what to watch for and copy a symptom form to send your provider.\n\nTrack: headaches, dizziness, concentration, sleep, mood — all the things that matter for your recovery stage.",
    icon: <Settings className="w-8 h-8 text-orange-400" />,
  },
  {
    id: 7,
    title: "Choose Your Tone",
    content: "Pick how your message sounds:\n\n• Professional — workplace formal\n• Civilian/Plain — everyday language\n• Concern/Urgent — something needs attention NOW\n• Detailed — include everything\n• Concise — just the essentials",
    icon: <Palette className="w-8 h-8 text-purple-400" />,
  },
  {
    id: 8,
    title: "Copy, Send, Done",
    content: "When your draft is ready:\n\n• Copy & paste into Genesis portal\n• Download as Word or PDF\n• Share via link to your phone\n• Email to yourself\n\nWalk into your appointment prepared.",
    icon: <Share2 className="w-8 h-8 text-blue-400" />,
  },
  {
    id: 9,
    title: "A Draft, Not a Diagnosis",
    content: "WriteRight doesn't diagnose or treat.\n\nYou describe → WriteRight drafts → Your provider reviews & finalizes.\n\nYour story matters. This tool helps you tell it clearly.",
    icon: <CheckCircle2 className="w-8 h-8 text-primary" />,
  },
];

/** Cinematic visual panels for slides without screenshot images */
const CinematicPanel = ({ type }) => {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setStep(s => (s + 1) % 6), 1800);
    return () => clearInterval(timer);
  }, []);

  if (type === 'autodetect') {
    const fields = [
      { label: 'Age', value: '34', color: 'text-emerald-400', delay: '0s' },
      { label: 'Gender', value: 'Male', color: 'text-emerald-400', delay: '0.3s' },
      { label: 'Status', value: 'Active Duty', color: 'text-emerald-400', delay: '0.6s' },
      { label: 'Reason', value: 'Low Back Pain', color: 'text-emerald-400', delay: '0.9s' },
      { label: 'Chief Complaint', value: 'Chronic LBP x3 months, radiculopathy', color: 'text-emerald-400', delay: '1.2s' },
      { label: 'ICD-10', value: 'M54.5, M54.16', color: 'text-amber-400', delay: '1.5s' },
    ];
    return (
      <div className="h-full min-h-[320px] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6 flex flex-col justify-center gap-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-500/60 font-medium mb-2 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          Auto-detecting from notes...
        </div>
        {fields.map((f, i) => (
          <div 
            key={f.label}
            className="flex items-center gap-3 opacity-0"
            style={{ 
              animation: `fadeSlideIn 0.5s ease-out forwards`,
              animationDelay: f.delay 
            }}
          >
            <span className="text-[11px] text-slate-500 w-24 text-right shrink-0 font-mono">{f.label}</span>
            <div className="h-px flex-1 bg-slate-700/50 max-w-8" />
            <span className={`text-sm font-semibold ${f.color} tracking-tight`}>{f.value}</span>
          </div>
        ))}
        <div className="mt-4 text-[10px] text-slate-600 italic text-center">
          Green text = detected live from your notes
        </div>
      </div>
    );
  }

  if (type === 'mood') {
    const moods = [
      { name: 'Default', colors: 'from-slate-800 to-navy-900', accent: '#d4a843', textClass: 'text-amber-400' },
      { name: 'Eggplant', colors: 'from-purple-950 via-purple-900 to-indigo-950', accent: '#c084fc', textClass: 'text-purple-300' },
      { name: 'Sunset', colors: 'from-orange-950 via-amber-900 to-red-950', accent: '#fb923c', textClass: 'text-orange-300' },
    ];
    const activeMood = moods[step % moods.length];
    return (
      <div 
        className={`h-full min-h-[320px] bg-gradient-to-br ${activeMood.colors} p-6 flex flex-col justify-center items-center transition-all duration-1000 ease-in-out`}
      >
        <div className="text-center space-y-4">
          <div 
            className="w-16 h-16 rounded-full mx-auto flex items-center justify-center transition-all duration-700"
            style={{ 
              background: `radial-gradient(circle, ${activeMood.accent}33, transparent)`,
              boxShadow: `0 0 40px ${activeMood.accent}22`
            }}
          >
            <Heart className={`w-8 h-8 ${activeMood.textClass} transition-colors duration-700`} />
          </div>
          <div>
            <p className={`text-lg font-bold ${activeMood.textClass} transition-colors duration-700`}>
              {activeMood.name}
            </p>
            <p className="text-xs text-white/40 mt-1">Current Theme</p>
          </div>
          <div className="flex gap-3 justify-center pt-2">
            {moods.map((m, i) => (
              <div 
                key={m.name}
                className={`w-8 h-8 rounded-full border-2 transition-all duration-500 ${
                  i === step % moods.length 
                    ? 'border-white/60 scale-125' 
                    : 'border-white/15 scale-100'
                }`}
                style={{ background: m.accent + '44' }}
              />
            ))}
          </div>
          <p className="text-[11px] text-white/30 max-w-[200px] mx-auto leading-relaxed pt-2">
            A micro-moment to check in with yourself before charting
          </p>
        </div>
      </div>
    );
  }

  return null;
};

const TutorialSlideshow = ({ type = 'provider', show = true, onClose, initialTab }) => {
  const [activeTab, setActiveTab] = useState(initialTab || type);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [animKey, setAnimKey] = useState(0);
  const slides = activeTab === 'provider' ? providerSlides : patientSlides;
  
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setCurrentSlide(0);
    setAnimKey(k => k + 1);
  };

  // Reset animation key when slide changes
  useEffect(() => {
    setAnimKey(k => k + 1);
  }, [currentSlide]);
  
  if (!show && !initialTab) return null;
  
  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      setCurrentSlide(0); // Carousel back to start
    }
  };
  
  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };
  
  const goToSlide = (index) => {
    setCurrentSlide(index);
  };
  
  const slide = slides[currentSlide];
  const hasImage = !!slide.image;
  const hasCinematic = !!slide.cinematic;
  const hasVisual = hasImage || hasCinematic;
  
  return (
    <div 
      className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 pb-16" 
      onClick={onClose}
    >
      <Card 
        className={`bg-card border-border shadow-2xl mb-12 ${hasVisual ? 'w-full max-w-4xl max-h-[80vh] overflow-y-auto' : 'w-full max-w-lg max-h-[80vh] overflow-y-auto'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <CardContent className="p-0">
          {/* Header with Tab Switcher */}
          <div className="flex items-center justify-between p-3 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="flex gap-1 p-1 bg-secondary/50 rounded-lg">
                <button
                  onClick={() => handleTabChange('patient')}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                    activeTab === 'patient'
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Patient
                </button>
                <button
                  onClick={() => handleTabChange('provider')}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                    activeTab === 'provider'
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Provider
                </button>
              </div>
              <span className="text-sm text-muted-foreground">
                {currentSlide + 1} / {slides.length}
              </span>
            </div>
            {/* Mar 3, 2026 — Explicit "Skip Tour" pill next to the X so
                first-time users don't feel forced to click Next through 9
                slides. Reported as friction blocker by real user testing. */}
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="h-8 px-3 text-xs text-muted-foreground hover:text-foreground"
                data-testid="tutorial-skip-tour"
              >
                Skip Tour
              </Button>
              <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0" data-testid="tutorial-close">
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {/* Slide Content - Click to advance */}
          <div 
            className={`cursor-pointer ${hasVisual ? 'flex flex-col lg:flex-row' : ''}`}
            onClick={nextSlide}
            title="Click anywhere to continue"
          >
            {/* Text Content - Left side when has image/cinematic */}
            <div className={`p-5 flex flex-col ${hasVisual ? 'lg:w-2/5 lg:border-r lg:border-border' : ''}`}>
              <div className="flex items-start gap-3 mb-3">
                <div className="shrink-0 p-2 bg-secondary/50 rounded-lg">
                  {slide.icon}
                </div>
                <div>
                  <h2 className="text-lg font-bold text-foreground leading-tight">
                    {slide.title}
                  </h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Tap to continue →
                  </p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed flex-1">
                {slide.content}
              </p>
            </div>
            
            {/* Screenshot with cinematic zoom - Right side */}
            {hasImage && (
              <div className="lg:w-3/5 p-4 bg-gradient-to-br from-amber-100 via-orange-50 to-yellow-100 flex flex-col overflow-hidden">
                <div className="relative flex-1">
                  <img 
                    key={animKey}
                    src={slide.image} 
                    alt={slide.title}
                    className={`w-full h-auto max-h-[350px] object-contain rounded-lg shadow-xl border-2 border-white/50 ${
                      slide.cinematicZoom ? 'animate-cinematic-zoom origin-top-left' : ''
                    }`}
                    style={slide.cinematicZoom ? { 
                      animation: 'cinematicZoom 8s ease-in-out infinite alternate',
                    } : {}}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  {/* Highlight indicator */}
                  {slide.highlight && (
                    <div className="absolute bottom-3 left-3 right-3 flex items-center gap-2 bg-black/80 text-white px-3 py-2 rounded-lg text-xs">
                      <MousePointer className="w-4 h-4 text-amber-400 shrink-0" />
                      <span>{slide.highlight}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Cinematic Visual Panel (no image file) */}
            {hasCinematic && !hasImage && (
              <div className="lg:w-3/5 p-0 overflow-hidden relative">
                <CinematicPanel key={animKey} type={slide.cinematicVisual} />
              </div>
            )}
          </div>
          
          {/* Progress Dots */}
          <div className="flex justify-center gap-1.5 py-3 border-t border-border bg-secondary/20">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={(e) => { e.stopPropagation(); goToSlide(index); }}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentSlide 
                    ? 'bg-primary w-6' 
                    : index < currentSlide 
                      ? 'bg-primary/50' 
                      : 'bg-muted-foreground/30'
                }`}
              />
            ))}
          </div>
          
          {/* Navigation Footer */}
          <div className="flex items-center justify-between p-3 border-t border-border bg-secondary/30">
            <Button 
              variant="outline" 
              onClick={(e) => { e.stopPropagation(); prevSlide(); }}
              disabled={currentSlide === 0}
              size="sm"
              className="gap-1"
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </Button>
            
            <div className="flex gap-2">
              {currentSlide === slides.length - 1 && (
                <Button 
                  onClick={(e) => { e.stopPropagation(); nextSlide(); }}
                  variant="outline" 
                  size="sm"
                  className="gap-1"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Start Over
                </Button>
              )}
              <Button 
                onClick={(e) => { e.stopPropagation(); currentSlide === slides.length - 1 ? onClose() : nextSlide(); }}
                size="sm"
                className="gap-1 bg-primary hover:bg-primary/90"
              >
                {currentSlide === slides.length - 1 ? (
                  <>
                    <CheckCircle2 className="w-4 h-4" />
                    Done
                  </>
                ) : (
                  <>
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TutorialSlideshow;
